import { PilotId } from '~/eds/src/types/PilotId';
import { SearchQuery } from '~/features/advanced-search/types';
import { PilotSearchQuery } from '~/redux/api/methods/search';
import {
  BooleanQueryEntity,
  EntityQuery,
  FilterQueryEntity,
  QueryOperatorEntity,
  SectionEntity,
} from '~/types/OpenSearch';

import { SavedSearch } from '../types/search';

export const testIsFilter = (
  queryEntity: EntityQuery,
): queryEntity is FilterQueryEntity => queryEntity.type === 'filter';

export const testIsSection = (
  queryEntity: EntityQuery,
): queryEntity is SectionEntity => queryEntity.type === 'section';

export const testIsBooleanTextSearch = (
  queryEntity: EntityQuery,
): queryEntity is BooleanQueryEntity => queryEntity.type === 'bool_text_search';

export const testIsOperator = (
  queryEntity: EntityQuery,
): queryEntity is QueryOperatorEntity => queryEntity.type === 'operator';

export const testIsQueryV2 = (
  savedSearch: SavedSearch,
): savedSearch is SavedSearch & { query: PilotSearchQuery } => {
  return savedSearch.version === 2;
};

export const testIsQueryV3 = (
  savedSearch: SavedSearch,
): savedSearch is SavedSearch & { query: SearchQuery } => {
  return savedSearch.version === 3;
};

export const testHasNestedSections = (query: SearchQuery) => {
  const firstLevelSections = query.filter(testIsSection);
  return firstLevelSections.some((section) =>
    section.value.some(testIsSection),
  );
};

export const testHasOrOperator = (query: SearchQuery) =>
  query.filter(testIsOperator).some((operator) => operator.value === 'or');

export const testHasMixedOperatorInSection = (query: SearchQuery) =>
  query
    .filter(testIsSection)
    .some((section) => testHasMixedOperators(section.value));

export const testHasMixedOperators = (query: SearchQuery) =>
  query
    .filter(testIsOperator)
    .some((operator, _, arr) =>
      operator.value === 'and' || operator.value === 'or'
        ? arr.some((op) => op.value !== operator.value)
        : false,
    );

export const getActiveFilterIds = (query: SearchQuery): PilotId[] => {
  return query.filter(testIsFilter).map((filter) => filter.id);
};

// Flattens section entities so a scope-merged query can be rebuilt as editable
// filters + text; sections aren't editable chips, so nested grouping becomes AND.
export const flattenQueryEntities = (entities: EntityQuery[]): EntityQuery[] =>
  entities.flatMap((entity) =>
    testIsSection(entity)
      ? flattenQueryEntities((entity as SectionEntity).value as EntityQuery[])
      : [entity],
  );

// Wraps bare top-level filters/bool_text_search in their own section
// so that the query stays editable in the query builder.
export const wrapBareEntitiesInSections = (
  entities: EntityQuery[],
): EntityQuery[] =>
  entities.map((entity) =>
    testIsOperator(entity) || testIsSection(entity)
      ? entity
      : ({ type: 'section', value: [entity] } as SectionEntity),
  );

// Only bool_text_search entities feed the shared search bar; structured
// filters (including name/content pairs) always stay separate chips.
export const extractSearchBarText = (
  entities: EntityQuery[],
): string | undefined =>
  [
    ...new Set(
      entities
        .filter(testIsBooleanTextSearch)
        .map((entity) => entity.value)
        .filter(Boolean),
    ),
  ].join(' ') || undefined;

// Drops repeated filters (same id/operator/values) so a multi-turn scope re-merge
// doesn't produce duplicate chips; operators/other entities pass through.
export const dedupeFilters = (entities: EntityQuery[]): EntityQuery[] => {
  const seen = new Set<string>();
  return entities.filter((entity) => {
    if (!testIsFilter(entity)) return true;
    const key = JSON.stringify([
      entity.id,
      entity.operator,
      (entity as FilterQueryEntity).value?.value_list,
    ]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
